/* eslint-disable react-hooks/refs */
import { ReactNode } from 'react';
import { useTranslation } from '@sib/i18n';
import { LoaderCircle } from '../../icons';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../Accordion';
import { Skeleton } from '../Skeleton';
import { ComboboxCategoryOption, ComboboxOption } from './Combobox';
import { ComboboxCommandItem } from './ComboboxCommandItem';

type ComboboxCollapsibleSectionProps<
  V extends string | string[] | null = string,
  T extends Record<string, unknown> = {},
> = {
  option: ComboboxCategoryOption<T>;
  isMultiSelect?: boolean;
  value?: V;
  toggleValue: (value: string) => void;
  onOptionSelect?: (o: ComboboxOption<T>) => void | boolean;
  setOpen: (open: boolean) => void;
  OptionComponent: (o: ComboboxOption<T>) => ReactNode;
  noResultMessage?: string;
};

export function ComboboxCollapsibleSection<
  V extends string | string[] | null = string,
  T extends Record<string, unknown> = {},
>({
  option,
  isMultiSelect,
  value,
  toggleValue,
  onOptionSelect,
  setOpen,
  OptionComponent,
  noResultMessage,
}: ComboboxCollapsibleSectionProps<V, T>) {
  const { t } = useTranslation();
  const categoryItems = option.categoryItems || [];
  const totalItems = option.totalItems ?? 0;
  const categoryHasNextPage = option.hasNextPage ?? false;
  const hasMatchingItems = categoryItems.length > 0;
  const isLoading = option.isLoading ?? false;

  return (
    <AccordionItem value={option.value} className="border-b">
      <AccordionTrigger className="border-border cursor-pointer px-4 py-2 text-sm font-medium hover:no-underline">
        <div className="text-primary flex items-center gap-2">
          <OptionComponent {...(option as ComboboxOption<T>)} />
          {isLoading ? (
            <LoaderCircle className="size-4 animate-spin" />
          ) : (
            <span>({totalItems})</span>
          )}
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-0 pb-0">
        {hasMatchingItems ? (
          <>
            {categoryItems.map((item, index) => (
              <ComboboxCommandItem
                key={`${item.value}-${index}`}
                option={item}
                isMultiSelect={isMultiSelect}
                value={value}
                toggleValue={toggleValue}
                onOptionSelect={onOptionSelect}
                setOpen={setOpen}
                OptionComponent={OptionComponent}
              />
            ))}
            {categoryHasNextPage && (
              <div
                ref={option.sentryRef}
                className="flex w-full items-center justify-center"
              >
                <Skeleton className="h-8 w-full" />
              </div>
            )}
          </>
        ) : (
          <div className="text-muted-foreground px-4 py-2 text-sm">
            {noResultMessage || t('noResults')}
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}
